<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Budget Management</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Budgets</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Budgets</h1>
                <p class="mb-0 small">View budget years</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <!-- <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Initiated:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div> -->

              <div v-if="error" class="alert alert-warning alert-dismissible fade show" role="alert">
                {{ error }}
                <button type="button" class="btn-close" aria-label="Close" @click.prevent="clearError()"></button>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 100%;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by name, total amount budgeted, total revenue amount or year..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading budget years...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Budget Year</th>
                        <th>Total Amount Budgeted</th>
                        <th>Total Amount Released</th>
                        <th>Balance</th>
                        <th>Performance</th>
                        <th>Total Revenue Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(budget, index) in paginatedItems" :key="budget.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength +
                          index + 1 }}</td>
                        <td data-label="Budget Year" class="primary-col fw-semibold" title="View Budget"
                          @click.prevent="ViewBudget(budget.year)" style="cursor: pointer;">
                          <router-link
                            :to="{ name: 'ViewBudgetYear', params: { year: budget.year } }"
                            class="view-link text-decoration-underline">
                            {{ budget.year }}
                          </router-link>
                        </td>
                        <td data-label="Total Amount Budgeted" class="secondary-col fw-semibold text-end">
                          {{ formatCurrency(budget.amountBudgeted || 0) }}</td>
                        <td data-label="Amount Budgeted" class="secondary-col fw-semibold text-end">{{
                          formatCurrency(budget.amountReleased || 0) }}</td>
                        <td data-label="Balance" class="secondary-col fw-semibold text-end">{{
                          formatCurrency(budget.balance || 0) }}</td>
                        <td data-label="Performance" class="secondary-col fw-semibold text-end">{{
                          Number(budget.performance).toFixed(2) + '%' || 'N/A' }}</td>
                        <td data-label="Total Revenue Amount" class="secondary-col fw-semibold text-end">{{
                          formatCurrency(budget.revenueAmount || 0) }}</td>
                      </tr>

                      <tr v-if="paginatedItems.length === 0">
                        <td :colspan="7" class="text-center text-muted py-4">
                          <div v-if="tableNotice">{{ tableNotice }}</div>
                          <div v-else>No budgets yet.</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const router = useRouter()

// const statusFilter = ref('')
// const filters = ref({
//   date: '',
//   status: ''
// })
const budgets = ref([])
const error = ref('')
const loading = ref(false);
const search = ref('');
const pageLength = ref(10);
const currentPage = ref(1);

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG')
}

const fetchBudgets = async () => {
  const token = Cookies.get('auth_token');
  error.value = ''
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/eli/mda-eli-budget/all-budget`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    console.log('Fetched Budgets:', response.data);

    budgets.value = response.data;
  } catch (error) {
    console.error('Failed to fetch Budgets:', error)
    error.value = 'Failed to fetch budgets. Please try again.'
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  let filtered = budgets.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(budget => {
      return (
        budget.name.toLowerCase().includes(term) ||
        String(budget.createdDate).toLowerCase().includes(term) ||
        budget.originatingMda.toLowerCase().includes(term) ||
        budget.amountBudgeted.toString().includes(term) ||
        budget.revenueAmount.toString().includes(term) ||
        budget.year.toString().includes(term)

      )
    })
  }

  // Apply date filter
  // if (filters.value.date) {
  //   const now = new Date()
  //   filtered = filtered.filter(w => {
  //     const createdDate = new Date(w.createdDate)
  //     switch (filters.value.date) {
  //       case 'this_month':
  //         return createdDate.getMonth() === now.getMonth() &&
  //           createdDate.getFullYear() === now.getFullYear()
  //       case 'last_month': {
  //         const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
  //         return createdDate.getMonth() === lastMonth.getMonth() &&
  //           createdDate.getFullYear() === lastMonth.getFullYear()
  //       }
  //       case 'this_year':
  //         return createdDate.getFullYear() === now.getFullYear()
  //       default:
  //         return true
  //     }
  //   })
  // }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    return new Date(b.createdDate) - new Date(a.createdDate)
  })

  // Apply status filter
  // if (filters.value.status) {
  //   filtered = filtered.filter(w => w.status.toLowerCase() === filters.value.status.toLowerCase())
  // }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

// watch(statusFilter, () => {
//   currentPage.value = 1
// })

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

// const clearFilters = () => {
//   filters.value = {
//     mda: '',
//     date: '',
//     status: ''
//   }
//   search.value = ''
// }

const ViewBudget = (budgetYear) => {
  if (!budgetYear) {
    error.value = 'Unable to open budget details: budget year is missing.'
    return
  }
  router.push({ name: 'ViewBudgetYear', params: { year: budgetYear } })
}

const clearError = () => {
  error.value = ''
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Budget Year', 'Total Amount Budgeted', 'Total Amount Released', 'Balance', 'Performance', 'Total Revenue Amount']],
    body: budgets.value.map((budget, index) => [
      index + 1,
      budget.year,
      budget.amountBudgeted,
      budget.amountReleased,
      budget.balance,
      budget.performance,
      budget.revenueAmount
    ])
  })
  doc.save('Budgets.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('budgets')
  worksheet.addRow(['S/N', 'Budget Year', 'Total Amount Budgeted', 'Total Amount Released', 'Balance', 'Performance', 'Total Revenue Amount'])

  budgets.value.forEach((budget, index) => {
    worksheet.addRow([
      index + 1,
      budget.year,
      budget.amountBudgeted,
      budget.amountReleased,
      budget.balance,
      budget.performance,
      budget.revenueAmount
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'budgets.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchBudgets(),
    document.title = "View Budgets"
})

</script>
<style scoped>
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.view-link {
  color: #428bca;
  font-weight: 500;
}

.view-link:hover {
  color: #2a6496;
  text-decoration: underline !important;
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--table-border);
}

.form-label {
  margin-bottom: 0.25rem;
    color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 1000px;
}

.register-wrapper {
  display: flex;
  max-height: 35px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}
</style>
